import React, { useEffect, useMemo, useState } from 'react';

import { getEvents as getArchivedEvents, getUpcomingEvents } from '../../api';
import {
  buildThemeEventPayload,
  filterEventsForTheme,
  getDiscoveryTheme,
  getThemeRequestFilters,
  normalizeDiscoveryTheme,
} from '../../lib/discoveryThemes';
import { EventLearningCard } from './EventLearningCard';

function prettyDate(value) {
  if (!value) return 'Timing TBD';
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function normalizeReasons(item) {
  if (Array.isArray(item?.why_it_matters)) return item.why_it_matters;
  if (Array.isArray(item?.expected_effects)) return item.expected_effects;
  if (Array.isArray(item?.tags)) return item.tags;
  if (typeof item?.reason === 'string' && item.reason) return [item.reason];
  if (typeof item?.source === 'string' && item.source) return [`Source: ${item.source}`];
  return [];
}

function normalizeArchiveEvent(item) {
  return {
    id: item?._id || item?.item_type_id || item?.id,
    title: item?.name || item?.title || item?.event_type || 'Untitled event',
    category: item?.category || 'event',
    category_label: item?.category_label || item?.category || 'Event',
    summary:
      item?.summary ||
      item?.description ||
      item?.reason ||
      'No short summary is available for this event yet.',
    status: item?.status || 'scheduled',
    starts_at: item?.starts_at || item?.start_ts || item?.start_date || '',
    distance_km: item?.distance_km ?? null,
    why_it_matters: normalizeReasons(item),
  };
}

function buildArchiveFallbackPayload(items) {
  const events = items.map(normalizeArchiveEvent);
  const activeCount = events.filter((item) => item.status === 'active').length;
  const upcomingCount = events.filter((item) => item.status !== 'active').length;

  return {
    summary: {
      event_count: events.length,
      active_count: activeCount,
      upcoming_count: upcomingCount,
    },
    events,
  };
}

export const UpcomingSignalsPanel = ({
  lat,
  lng,
  theme = 'wildlife',
  title,
  description,
}) => {
  const activeTheme = normalizeDiscoveryTheme(theme);
  const activeThemeConfig = getDiscoveryTheme(activeTheme);
  const themeFilters = useMemo(() => getThemeRequestFilters(activeTheme), [activeTheme]);
  const [manualLat, setManualLat] = useState(lat ?? 36.95);
  const [manualLng, setManualLng] = useState(lng ?? -122.1);
  const [days, setDays] = useState(14);
  const [radiusKm, setRadiusKm] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [sourceMode, setSourceMode] = useState('live');

  useEffect(() => {
    if (typeof lat === 'number' && Number.isFinite(lat)) setManualLat(lat);
  }, [lat]);

  useEffect(() => {
    if (typeof lng === 'number' && Number.isFinite(lng)) setManualLng(lng);
  }, [lng]);

  useEffect(() => {
    if (!Number.isFinite(Number(manualLat)) || !Number.isFinite(Number(manualLng))) {
      return;
    }

    let active = true;

    async function loadSignals() {
      setLoading(true);
      setError('');
      setSourceMode('live');

      try {
        const { data } = await getUpcomingEvents({
          lat: Number(manualLat),
          lon: Number(manualLng),
          radiusKm,
          days,
          interests: themeFilters.interests,
          categories: themeFilters.categories,
        });

        if (!active) return;
        setPayload(buildThemeEventPayload(data, activeTheme));
        setSourceMode('live');
      } catch (err) {
        try {
          const { data } = await getArchivedEvents(1);
          if (!active) return;

          const themeFiltered = filterEventsForTheme(data?.data || [], activeTheme);

          setPayload(buildArchiveFallbackPayload(themeFiltered.slice(0, 8)));
          setSourceMode('archive');
        } catch (fallbackError) {
          if (!active) return;
          setError(
            err?.response?.data?.error ||
              fallbackError?.response?.data?.error ||
              err?.message ||
              'Failed to load upcoming signals.'
          );
        }
      } finally {
        if (!active) return;
        setLoading(false);
      }
    }

    loadSignals();

    return () => {
      active = false;
    };
  }, [activeTheme, days, manualLat, manualLng, radiusKm, themeFilters]);

  const resolvedTitle = title || activeThemeConfig.events.panelTitle;
  const resolvedDescription = description || activeThemeConfig.events.panelDescription;
  const summary = payload?.summary || {};
  const topEvents = Array.isArray(payload?.events) ? payload.events.slice(0, 4) : [];

  const useBrowserLocation = () => {
    if (!navigator.geolocation) {
      setError('Browser geolocation is unavailable on this device.');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setManualLat(Number(position.coords.latitude.toFixed(4)));
        setManualLng(Number(position.coords.longitude.toFixed(4)));
      },
      () => {
        setError('Unable to access browser location right now.');
      }
    );
  };

  const archiveNote = useMemo(() => {
    if (sourceMode !== 'archive') return '';
    return 'Live upcoming signals are unavailable right now, so this panel is showing the published Tellus event archive instead.';
  }, [sourceMode]);

  return (
    <section className="atlas-sidebar-card atlas-signal-panel">
      <div className="atlas-panel-header">
        <div className="atlas-sidebar-copy atlas-sidebar-copy--flush">
          <h3>{resolvedTitle}</h3>
          <p>{resolvedDescription}</p>
        </div>
        <button
          type="button"
          className="atlas-secondary-link atlas-control-button"
          onClick={useBrowserLocation}
        >
          Use my location
        </button>
      </div>

      <div className="atlas-control-grid">
        <label className="atlas-control-label">
          Latitude
          <input
            className="atlas-control-input"
            type="number"
            step="0.01"
            value={manualLat}
            onChange={(event) => setManualLat(event.target.value)}
          />
        </label>
        <label className="atlas-control-label">
          Longitude
          <input
            className="atlas-control-input"
            type="number"
            step="0.01"
            value={manualLng}
            onChange={(event) => setManualLng(event.target.value)}
          />
        </label>
        <label className="atlas-control-label">
          Days ahead
          <select
            className="atlas-control-input"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            <option value={7}>7</option>
            <option value={14}>14</option>
            <option value={30}>30</option>
          </select>
        </label>
        <label className="atlas-control-label">
          Radius km
          <select
            className="atlas-control-input"
            value={radiusKm}
            onChange={(event) => setRadiusKm(Number(event.target.value))}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        </label>
      </div>

      <div className="atlas-mini-stats">
        <div>
          <strong>{summary.event_count ?? 0}</strong>
          <span>events</span>
        </div>
        <div>
          <strong>{summary.active_count ?? 0}</strong>
          <span>active</span>
        </div>
        <div>
          <strong>{summary.upcoming_count ?? 0}</strong>
          <span>upcoming</span>
        </div>
      </div>

      {archiveNote ? <div className="atlas-detail-note">{archiveNote}</div> : null}
      {loading ? <div className="atlas-empty-state">Loading current signals…</div> : null}
      {!loading && error ? <div className="atlas-empty-state">{error}</div> : null}
      {!loading && !error && !topEvents.length ? (
        <div className="atlas-empty-state">No nearby signals are scheduled for this theme in the current window.</div>
      ) : null}

      {!loading && !error && topEvents.length ? (
        <div className="atlas-upcoming-grid">
          {topEvents.map((item) => (
            <article key={item.id || item.title} className="atlas-upcoming-card">
              <span className="atlas-card-kicker">{item.category_label || item.category}</span>
              <h4>{item.title}</h4>
              <p>{item.summary}</p>
              <div className="atlas-detail-stack">
                <span>
                  <strong>Status</strong>
                  {item.status}
                </span>
                <span>
                  <strong>Starts</strong>
                  {prettyDate(item.starts_at)}
                </span>
                <span>
                  <strong>Distance</strong>
                  {item.distance_km != null
                    ? `${item.distance_km.toFixed(1)} km`
                    : sourceMode === 'archive'
                      ? 'Archive-wide signal'
                      : 'Broad-region signal'}
                </span>
              </div>
              {Array.isArray(item.why_it_matters) && item.why_it_matters.length ? (
                <ul className="atlas-bullet-list atlas-bullet-list--compact">
                  {item.why_it_matters.slice(0, 2).map((reason) => (
                    <li key={reason}>{reason}</li>
                  ))}
                </ul>
              ) : null}
              <EventLearningCard item={item} />
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
};
